/* ===================================================================
   Reports -- PIXEL-FAITHFUL rebuild of the Claude Design mockup
   ("Rhody Reviews.dc.html", REPORTS screen; desktop 1279-1392, mobile
   2499-2542). Corbato, 2026-07-03.

   Deliberate, literal reproduction of the mockup's visual output: sticky
   header with a range pill + Export control, a 4-up KPI row, two trend
   charts (total-reviews-over-time + review volume), a rating breakdown,
   and a review-source panel. Styling lives in styles/reports.css, values
   lifted verbatim from the mockup.

   DATA WIRING PRESERVED. Every live hook and the CSV export from the prior
   Reports are kept:
     - useReports  -> KPIs + chart series + by_platform split (GET /reports)
     - useReviewList -> real per-star rating distribution
     - api.exportReportsCsv -> the Export control (token-auth CSV download)
     - range presets stored in the URL (?range=) so the view is shareable

   HARD RULE -- NO FABRICATED METRICS. The mockup shows several seeded demo
   figures the /reports payload does NOT provide (a monthly narrative
   snapshot, "+X vs prior period" deltas, a request->review conversion KPI,
   a local rating rank). We do not invent them. Where the backend has no
   source, we render "-" (matching the prior Reports' avg-rating treatment)
   or omit the panel, rather than fabricate a number.
   =================================================================== */
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, type ReportsSeries } from '../lib/api';
import { useReports, useReviewList, useMe } from '../hooks';
import { buildAndDownloadReportPdf } from '../lib/reportPdf';
import '../styles/reports.css';

/* ---- Date-range presets ----
   The /reports payload is a fixed weekly + ~6-month monthly window. Presets
   FILTER the already-fetched series client-side (no backend call); the
   selected range is stored in the URL (?range=) so a view is shareable. The
   labels mirror the mockup's range pill (6 months / 12 months / Year to
   date). If a preset reaches past the data we have, we surface an honest gap
   note rather than inventing buckets. Default (no ?range=) is 12 months. */
type RangeKey = '6m' | '12m' | 'ytd';
const RANGE_PRESETS: { key: RangeKey; label: string; short: string }[] = [
  { key: '6m', label: '6 months', short: '6 months' },
  { key: '12m', label: '12 months', short: '12 months' },
  { key: 'ytd', label: 'Year to date', short: 'YTD' },
];
function isRangeKey(v: string | null): v is RangeKey {
  return v === '6m' || v === '12m' || v === 'ytd';
}

/* Resolve a series point to an approximate calendar date for filtering.
   - Weekly key "2026-W18" -> that ISO week's Monday.
   - Monthly key "2026-06" -> the first of that month.
   Returns null when a date cannot be derived (then we keep the point). */
function periodDate(period: string): Date | null {
  if (!period) return null;
  const wk = period.match(/^(\d{4})-W(\d{1,2})$/);
  if (wk) {
    const year = Number(wk[1]);
    const week = Number(wk[2]);
    const jan4 = new Date(Date.UTC(year, 0, 4));
    const jan4Dow = (jan4.getUTCDay() + 6) % 7; // Mon=0
    const monday = new Date(jan4);
    monday.setUTCDate(jan4.getUTCDate() - jan4Dow + (week - 1) * 7);
    return monday;
  }
  const mo = period.match(/^(\d{4})-(\d{2})$/);
  if (mo) return new Date(Date.UTC(Number(mo[1]), Number(mo[2]) - 1, 1));
  const d = new Date(period);
  return isNaN(d.getTime()) ? null : d;
}

/* The earliest calendar date a preset wants to include (UTC). */
function rangeStart(range: RangeKey): Date | null {
  const now = new Date();
  if (range === '6m') return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 6, 1));
  if (range === 'ytd') return new Date(Date.UTC(now.getUTCFullYear(), 0, 1));
  // 12m
  return new Date(Date.UTC(now.getUTCFullYear() - 1, now.getUTCMonth(), 1));
}

/* Filter a series to the points on/after the preset's start date. Points whose
   date can't be derived are kept (honest -- never silently dropped). */
function filterSeries(series: ReportsSeries['series'], range: RangeKey): ReportsSeries['series'] {
  const start = rangeStart(range);
  if (!start) return series;
  return series.filter((p) => {
    const d = periodDate(p.period);
    return d == null || d >= start;
  });
}

/* Cumulative running total over a value series (nulls treated as 0 for the
   running sum, but a leading run of nulls stays null so an empty chart reads
   honestly). Powers "Total reviews over time". */
function cumulative(series: ReportsSeries['series']): ReportsSeries['series'] {
  let running = 0;
  let seenReal = false;
  return series.map((p) => {
    if (p.value != null) {
      running += p.value;
      seenReal = true;
    }
    return { ...p, value: seenReal ? running : null };
  });
}

/* Compact x-axis tick. Weekly "Jun 8-14" -> "8"; monthly "Jun 2026" -> "Jun".
   To avoid clutter on dense series, only every other tick shows past 8 buckets. */
function shortTick(label: string, i: number, total: number): string {
  if (!label) return '';
  if (total > 8 && i % 2 === 1) return '';
  const monthDay = label.match(/^([A-Za-z]{3})\s+(\d+)/);
  if (monthDay) return /\d{4}/.test(label) ? monthDay[1] : monthDay[2];
  return label.split(' ')[0];
}

/* Faithful single-series bar chart (mockup gradient bars). Bars scale to the
   data's own min/max; null values render as an empty gap. `soft` draws the
   pale-blue variant used for the "requests sent" underlay. */
function BarChart({ series, emptyNote, soft }: { series: ReportsSeries['series']; emptyNote: string; soft?: boolean }) {
  const values = series.map((p) => p.value).filter((v): v is number => v != null);
  if (values.length === 0) return <div className="rhr-chart-empty">{emptyNote}</div>;
  const max = Math.max(...values);
  const min = Math.min(...values);
  const span = max - min || 1;
  return (
    <>
      <div className="rhr-chart">
        {series.map((p, i) => {
          const h = p.value == null ? 0 : Math.max(6, Math.round(((p.value - min) / span) * 100));
          return (
            <div className="rhr-chart__col" key={p.period || i} title={`${p.label}: ${p.value ?? 'no data'}`}>
              <div className={'rhr-chart__bar' + (soft ? ' rhr-chart__bar--soft' : '')} style={{ height: h + '%' }} />
            </div>
          );
        })}
      </div>
      <div className="rhr-chart__ticks">
        {series.map((p, i) => (
          <span className="rhr-chart__tick" key={p.period || i}>
            {shortTick(p.label, i, series.length)}
          </span>
        ))}
      </div>
    </>
  );
}

/* Star row for the rating breakdown (mockup gold stars, N filled). */
function DistStars({ n }: { n: number }) {
  return (
    <span className="rhr-dist__stars">
      {[0, 1, 2, 3, 4].map((i) => (
        <svg key={i} viewBox="0 0 16 16" fill={i < n ? '#fbbc04' : '#dadce0'}>
          <path d="M8 1l2 4.5 5 .4-3.8 3.3 1.1 4.8L8 11.6 3.7 14l1.1-4.8L1 5.9l5-.4z" />
        </svg>
      ))}
    </span>
  );
}

const GoogleG = ({ s = 18 }: { s?: number }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="#4285F4">
    <path d="M12 11v2.6h4.3c-.2 1.1-1.4 3.2-4.3 3.2a4.8 4.8 0 0 1 0-9.6c1.5 0 2.5.6 3 1.1l2-2A7.5 7.5 0 1 0 12 19.5c4.3 0 7.2-3 7.2-7.3 0-.5 0-.9-.1-1.2z" />
  </svg>
);

/* Review SOURCE colours. Google only (2026-08-06, Steve): review ingestion is
   Google-only and has been since 2026-06-28 -- monitor_agent._sync_platform
   hard-refuses yelp and facebook regardless of config, and baseline_import
   returns before any Yelp or Facebook call. Facebook (#1877F2) and Yelp
   (#D32323) entries were removed from this map because keeping brand colours
   ready for them implied Rhody Reviews pulls reviews from those directories.
   It does not. Anything unexpected falls back to neutral grey and is still
   rendered with its real count rather than dropped, so the chart can never
   silently hide a row. (Findable on a directory is a different thing and stays
   multi-directory on the Listings/NAP screen.) */
const PLATFORM_COLORS: Record<string, string> = {
  google: '#4285F4',
};
function platformColor(p: string): string {
  return PLATFORM_COLORS[p.toLowerCase()] ?? '#9aa0a6';
}
function platformLabel(p: string): string {
  if (!p) return 'Other';
  return p.charAt(0).toUpperCase() + p.slice(1);
}

export function Reports() {
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(false);
  // U9: brief "Downloaded" acknowledgement after a successful CSV/PDF export.
  const [exportDone, setExportDone] = useState(false);
  useEffect(() => {
    if (!exportDone) return;
    const t = window.setTimeout(() => setExportDone(false), 2600);
    return () => window.clearTimeout(t);
  }, [exportDone]);
  const [pdfExporting, setPdfExporting] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const { data, isLoading, isError, refetch } = useReports();
  const reviews = useReviewList({ limit: 500 });
  const me = useMe();

  // Range preset, stored in the URL (?range=) so the view is shareable.
  const [searchParams, setSearchParams] = useSearchParams();
  const rangeParam = searchParams.get('range');
  const range: RangeKey = isRangeKey(rangeParam) ? rangeParam : '12m';
  function setRange(next: RangeKey) {
    setSearchParams(
      (prev) => {
        const sp = new URLSearchParams(prev);
        if (next === '12m') sp.delete('range');
        else sp.set('range', next);
        return sp;
      },
      { replace: true },
    );
  }

  const kpis = data?.kpis;
  const avgRating = kpis?.avg_rating ?? null;
  const replyRatePct = kpis?.reply_rate != null ? Math.round(kpis.reply_rate * 100) : null;
  const weeks = kpis?.new_reviews_window_weeks ?? 12;

  const fullVolumeSeries = data?.review_count_by_week.series ?? [];
  const byPlatform = data?.by_platform ?? [];

  // Apply the preset client-side over the already-fetched series.
  const volumeSeries = useMemo(() => filterSeries(fullVolumeSeries, range), [fullVolumeSeries, range]);
  const cumulativeSeries = useMemo(() => cumulative(volumeSeries), [volumeSeries]);

  // Honest gap: a preset the data does not fully cover.
  const gapNote = useMemo(() => {
    const have = fullVolumeSeries.length;
    const shown = volumeSeries.length;
    if (have === 0) return null;
    if (shown === 0) return 'No data in this range yet. Try a wider range.';
    return null;
  }, [fullVolumeSeries, volumeSeries]);

  // Real per-star rating distribution derived from the live review list.
  // Only rendered when we actually have rated reviews -- never seeded.
  const ratingDist = useMemo(() => {
    const counts = [0, 0, 0, 0, 0]; // index 0 = 1 star ... index 4 = 5 star
    for (const r of reviews) {
      const s = Math.round(r.rating);
      if (s >= 1 && s <= 5) counts[s - 1] += 1;
    }
    const total = counts.reduce((a, b) => a + b, 0);
    return { counts, total };
  }, [reviews]);

  // Review-source split: real by_platform when present, else fall back to the
  // total_reviews as a single Google row (Google-only sourcing rule).
  const sourceRows = useMemo(() => {
    if (byPlatform.length > 0) return byPlatform.map((p) => ({ platform: p.platform, count: p.count }));
    const total = kpis?.total_reviews ?? 0;
    return total > 0 ? [{ platform: 'google', count: total }] : [];
  }, [byPlatform, kpis]);
  const sourceMax = useMemo(() => sourceRows.reduce((m, r) => Math.max(m, r.count), 0), [sourceRows]);
  const sourceTotal = useMemo(() => sourceRows.reduce((s, r) => s + r.count, 0), [sourceRows]);

  async function handleExport() {
    setExportError(false);
    setExporting(true);
    try {
      const csv = await api.exportReportsCsv();
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `rhody-reviews-report-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setExportDone(true);
    } catch {
      setExportError(true);
    } finally {
      setExporting(false);
    }
  }

  // Real PDF export of the report the screen shows. Builds client-side from
  // the SAME live data the body renders (KPIs + rating distribution + review
  // source + the range-filtered volume series). No fabricated metrics: null
  // KPIs render as "-" in the PDF exactly as on screen. CSV export is kept.
  function handleExportPdf() {
    setPdfError(false);
    if (!data) {
      setPdfError(true);
      return;
    }
    setPdfExporting(true);
    try {
      buildAndDownloadReportPdf({
        businessName: me.data?.business_name ?? '',
        rangeLabel: RANGE_PRESETS.find((p) => p.key === range)?.label ?? '',
        data,
        ratingDist,
        volumeSeries,
      });
      setExportDone(true);
    } catch {
      setPdfError(true);
    } finally {
      setPdfExporting(false);
    }
  }

  const ExportBtn = (
    <>
      {(exportError || pdfError) && (
        <span className="rhr-export__err">Export failed. Try again.</span>
      )}
      {exportDone && !exportError && !pdfError && (
        <span className="rhr-export__ok" role="status">Downloaded</span>
      )}
      <button
        className="rhr-export"
        onClick={handleExportPdf}
        disabled={pdfExporting || isLoading || !data}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <path d="M12 3v12M7 10l5 5 5-5M5 21h14" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        {pdfExporting ? 'Exporting...' : 'Export PDF'}
      </button>
      <button
        className="rhr-export rhr-export--ghost"
        onClick={handleExport}
        disabled={exporting}
      >
        {exporting ? 'Exporting...' : 'CSV'}
      </button>
    </>
  );

  const RangePill = (
    <div className="rhr-rangepill" role="tablist" aria-label="Date range">
      {RANGE_PRESETS.map((p) => (
        <button
          key={p.key}
          role="tab"
          aria-selected={range === p.key}
          className={range === p.key ? 'is-active' : ''}
          onClick={() => setRange(p.key)}
        >
          {p.label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="rhf-reports" data-tour="reports">
      {/* STICKY HEADER (desktop) */}
      <div className="rhr-head">
        <div>
          <h1 className="rhr-head__h">Reports</h1>
          <p className="rhr-head__p">How your reputation is trending -- all from your Google reviews.</p>
        </div>
        <div className="rhr-head__tools">
          {RangePill}
          {ExportBtn}
        </div>
      </div>

      <div className="rhr-body">
        {/* MOBILE HEADER (inline; hidden on desktop) */}
        <div className="rhr-mhead">
          <div className="rhr-mhead__h">Reports</div>
          <div className="rhr-mhead__s">Your reputation, trending</div>
        </div>
        <div className="rhr-mrange">{RANGE_PRESETS.map((p) => (
          <button
            key={p.key}
            className={'rhr-mrange-btn'}
            style={{
              flexShrink: 0,
              border: 0,
              cursor: 'pointer',
              fontFamily: 'var(--inter)',
              fontSize: 13,
              fontWeight: 600,
              padding: '8px 16px',
              borderRadius: 999,
              whiteSpace: 'nowrap',
              // Selected pill uses the DEEP brand blue: white on #1a73e8
              // measured 4.37:1 rendered (fails AA); #0b47a1 is 8.42:1.
              background: range === p.key ? '#0b47a1' : '#eef0f3',
              color: range === p.key ? '#fff' : '#5f6368',
            }}
            aria-selected={range === p.key}
            onClick={() => setRange(p.key)}
          >
            {p.short}
          </button>
        ))}</div>

        {isError ? (
          <div className="rhr-card rhr-error">
            <p>Could not load your reports.</p>
            <button className="rhr-retry" onClick={() => refetch()}>Try again</button>
          </div>
        ) : (
          <>
            {gapNote && <span className="rhr-gap">{gapNote}</span>}

            {/* KPI ROW */}
            <div className="rhr-kpirow">
              <div className="rhr-kpi">
                <div className="rhr-kpi__lab">AVERAGE RATING</div>
                <div className="rhr-kpi__num">
                  {isLoading ? '...' : avgRating == null ? <span className="rhr-kpi__sub--muted">-</span> : (
                    <>
                      {avgRating.toFixed(1)}
                      <span className="rhr-kpi__star"> {'★'}</span>
                    </>
                  )}
                </div>
                <div className="rhr-kpi__sub">{kpis ? `${kpis.avg_rating_n} reviews` : 'Across your reviews'}</div>
              </div>

              <div className="rhr-kpi">
                <div className="rhr-kpi__lab">NEW REVIEWS</div>
                <div className="rhr-kpi__num">{isLoading ? '...' : (kpis?.new_reviews ?? 0).toLocaleString()}</div>
                <div className="rhr-kpi__sub">Last {weeks} weeks</div>
              </div>

              <div className="rhr-kpi">
                <div className="rhr-kpi__lab">RESPONSE RATE</div>
                <div className="rhr-kpi__num">
                  {isLoading ? '...' : replyRatePct == null ? <span className="rhr-kpi__sub--muted">-</span> : (
                    <>
                      {replyRatePct}
                      <span className="rhr-kpi__pct">%</span>
                    </>
                  )}
                </div>
                <div className="rhr-kpi__sub">
                  {kpis ? `${kpis.reply_rate_replied}/${kpis.reply_rate_total} replied` : 'Replied'}
                </div>
              </div>

              <div className="rhr-kpi">
                <div className="rhr-kpi__lab">TOTAL REVIEWS</div>
                <div className="rhr-kpi__num">{isLoading ? '...' : (kpis?.total_reviews ?? 0).toLocaleString()}</div>
                <div className="rhr-kpi__sub">All time</div>
              </div>
            </div>

            {/* CHARTS ROW */}
            <div className="rhr-charts">
              <div className="rhr-card">
                <div className="rhr-card__title">Total reviews over time</div>
                <div className="rhr-card__sub">Cumulative Google reviews, period by period.</div>
                {isLoading ? (
                  <div className="rhr-chart-empty">Loading...</div>
                ) : (
                  <BarChart series={cumulativeSeries} emptyNote="No reviews in this range yet." />
                )}
              </div>

              <div className="rhr-card">
                <div className="rhr-card__title">Review volume</div>
                <div className="rhr-legend">
                  <span><i style={{ background: '#1a73e8' }} />Reviews earned</span>
                </div>
                {isLoading ? (
                  <div className="rhr-chart-empty">Loading...</div>
                ) : (
                  <BarChart series={volumeSeries} emptyNote="No reviews in this range yet." />
                )}
              </div>
            </div>

            {/* SPLIT: RATING BREAKDOWN + REVIEW SOURCE */}
            <div className="rhr-split">
              <div className="rhr-card">
                <div className="rhr-card__title">Rating breakdown</div>
                {ratingDist.total === 0 ? (
                  <div className="rhr-chart-empty" style={{ height: 'auto', padding: '20px 0' }}>
                    No rated reviews yet.
                  </div>
                ) : (
                  <div className="rhr-dist">
                    {[5, 4, 3, 2, 1].map((star) => {
                      const count = ratingDist.counts[star - 1];
                      const pct = ratingDist.total ? Math.round((count / ratingDist.total) * 100) : 0;
                      return (
                        <div className="rhr-dist__row" key={star}>
                          <DistStars n={star} />
                          <div className="rhr-dist__track">
                            <div className="rhr-dist__fill" style={{ width: pct + '%' }} />
                          </div>
                          <div className="rhr-dist__count">{count}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="rhr-card">
                <div className="rhr-card__title">Review source</div>
                {sourceRows.length === 0 ? (
                  <div className="rhr-chart-empty" style={{ height: 'auto', padding: '20px 0' }}>
                    No reviews to break down yet.
                  </div>
                ) : (
                  <>
                    {sourceRows.map((r) => {
                      const color = platformColor(r.platform);
                      const pct = sourceMax ? Math.round((r.count / sourceMax) * 100) : 0;
                      return (
                        <div key={r.platform}>
                          <div className="rhr-src__row">
                            <div className="rhr-src__name">
                              {r.platform.toLowerCase() === 'google' && <GoogleG />}
                              {platformLabel(r.platform)}
                            </div>
                            <div className="rhr-src__count">{r.count.toLocaleString()}</div>
                          </div>
                          <div className="rhr-src__track">
                            <div className="rhr-src__fill" style={{ width: pct + '%', background: color }} />
                          </div>
                        </div>
                      );
                    })}
                    <div className="rhr-src__foot">
                      {sourceRows.length === 1 && sourceRows[0].platform.toLowerCase() === 'google'
                        ? '100% of your reviews come from Google, your only connected review source.'
                        : `${sourceTotal.toLocaleString()} reviews across your connected sources.`}
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
